/**
 * An inert snapshot of a loaded model, registered as the "model" resource in
 * place of the live gltf `result`. Holding the result pinned
 * `result.parser.associations` (a Map of every parsed object) → the whole scene
 * graph + GPU textures. The projection carries ONLY data — never the `parser`
 * (its `associations`) or `parser.options` (which references the LoadingManager +
 * DRACO/KTX2 decoders), and json with embedded `data:` URIs stripped — so it pins
 * nothing on the heap yet still describes the file for the inspector. Size
 * estimates are computed once here (from the live result) and stored as numbers.
 */

/** Non-enumerable brand so the property provider can recognise a projection
 *  (it isn't an Object3D/gltf-result shape) without a field showing up in the
 *  generic property walk. */
const MODEL_PROJECTION_BRAND = Symbol("needle.modelProjection");

/** True for an object built by {@link buildModelProjection}. */
export function isModelProjection(obj: unknown): obj is ModelProjection {
    return !!obj && typeof obj === "object" && (obj as Record<symbol, unknown>)[MODEL_PROJECTION_BRAND] === true;
}

export interface AnimationProjection {
    name: string;
    trackCount: number;
    keyframeCount: number;
    duration?: number;
}

/** Rough size estimates by resource kind (runtime, not disk), pre-formatted as
 *  human-readable strings ("106.7 MB", "545 KB", "0 B") — a top-level item. */
export interface ModelSizeEstimate {
    textures: string;
    meshes: string;
    animations: string;
    json: string;
}

type SizeBytes = { textures: number; meshes: number; animations: number; json: number };

export interface ModelProjection {
    name?: string;
    animations: AnimationProjection[];
    nodeCount?: number;
    meshCount?: number;
    materialCount?: number;
    textureCount?: number;
    imageCount?: number;
    extensionsUsed?: string[];
    sizeEstimate: ModelSizeEstimate;
    /** glTF json (structure only, `data:` URIs stripped) — undefined for non-gltf. */
    json?: unknown;
}

type ObjLike = {
    isObject3D?: boolean;
    children?: ObjLike[];
    geometry?: GeoLike;
    material?: MatLike | MatLike[];
    name?: string;
};
type GeoLike = { attributes?: Record<string, { array?: { byteLength?: number } }>; index?: { array?: { byteLength?: number } } };
type MatLike = Record<string, unknown>;
type ClipLike = {
    name?: string;
    duration?: number;
    tracks?: Array<{ times?: { length?: number; byteLength?: number }; values?: { byteLength?: number } }>;
};
type GltfLike = ObjLike & {
    scene?: ObjLike | null;
    scenes?: ObjLike[];
    animations?: ClipLike[];
    parser?: { json?: Record<string, unknown> } | null;
};

export function buildModelProjection(result: unknown): ModelProjection {
    const r = (result ?? {}) as GltfLike;
    const root = rootOf(r);

    const projection: ModelProjection = {
        name: r.scene?.name ?? r.name,
        animations: Array.isArray(r.animations) ? r.animations.map(clipProjection) : [],
        sizeEstimate: formatSizes(estimateSizes(root, r.animations, r.parser?.json)),
    };

    const json = r.parser?.json;
    if (json && typeof json === "object") {
        const len = (key: string) => (Array.isArray(json[key]) ? (json[key] as unknown[]).length : undefined);
        projection.nodeCount = len("nodes");
        projection.meshCount = len("meshes");
        projection.materialCount = len("materials");
        projection.textureCount = len("textures");
        projection.imageCount = len("images");
        projection.extensionsUsed = Array.isArray(json.extensionsUsed) ? (json.extensionsUsed as string[]) : undefined;
        projection.json = stripDataUris(json);
    }

    Object.defineProperty(projection, MODEL_PROJECTION_BRAND, { value: true }); // non-enumerable brand
    return projection;
}

/** The in-scene root of a load result (gltf.scene, or the Group for FBX/OBJ). */
function rootOf(r: GltfLike): ObjLike | null {
    if (r?.scene?.isObject3D) return r.scene;
    if (r?.isObject3D) return r;
    if (Array.isArray(r?.scenes) && r.scenes[0]?.isObject3D) return r.scenes[0];
    return null;
}

function clipProjection(clip: ClipLike): AnimationProjection {
    const tracks = Array.isArray(clip?.tracks) ? clip.tracks : [];
    return {
        name: typeof clip?.name === "string" ? clip.name : "",
        trackCount: tracks.length,
        keyframeCount: tracks.reduce((sum, t) => sum + (t?.times?.length ?? 0), 0),
        duration: typeof clip?.duration === "number" ? clip.duration : undefined,
    };
}

/**
 * Rough runtime byte estimates. Computed here while the result is live; only the
 * NUMBERS are kept. Geometries/textures deduped (shared instances counted once).
 */
/** Human-readable byte string, auto-scaled (B/KB/MB/GB). */
function formatBytes(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    const decimals = i === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2;
    return `${v.toFixed(decimals)} ${units[i]}`;
}

function formatSizes(b: SizeBytes): ModelSizeEstimate {
    return { textures: formatBytes(b.textures), meshes: formatBytes(b.meshes), animations: formatBytes(b.animations), json: formatBytes(b.json) };
}

function estimateSizes(root: ObjLike | null, animations: ClipLike[] | undefined, json: unknown): SizeBytes {
    const seenGeo = new Set<object>();
    const seenTex = new Set<object>();
    let meshes = 0;
    let textures = 0;

    const visit = (obj: ObjLike | undefined): void => {
        if (!obj) return;
        const geo = obj.geometry;
        if (geo && !seenGeo.has(geo)) { seenGeo.add(geo); meshes += geometryBytes(geo); }
        const mats = Array.isArray(obj.material) ? obj.material : obj.material ? [obj.material] : [];
        for (const m of mats) for (const tex of texturesOf(m)) {
            if (!seenTex.has(tex)) { seenTex.add(tex); textures += textureBytes(tex); }
        }
        obj.children?.forEach(visit);
    };
    visit(root ?? undefined);

    let anim = 0;
    for (const clip of animations ?? []) for (const t of clip?.tracks ?? []) {
        anim += (t?.times?.byteLength ?? 0) + (t?.values?.byteLength ?? 0);
    }

    let jsonBytes = 0;
    try { jsonBytes = json ? JSON.stringify(json).length : 0; } catch { /* cyclic — skip */ }

    return { textures, meshes, animations: anim, json: jsonBytes };
}

function geometryBytes(geo: GeoLike): number {
    let bytes = 0;
    const attrs = geo.attributes ?? {};
    for (const key in attrs) bytes += attrs[key]?.array?.byteLength ?? 0;
    bytes += geo.index?.array?.byteLength ?? 0;
    return bytes;
}

/** texture-valued properties of a material (map, normalMap, …) by duck-type. */
function texturesOf(mat: MatLike): object[] {
    const out: object[] = [];
    for (const key in mat) {
        const v = mat[key];
        if (v && typeof v === "object" && (v as { isTexture?: boolean }).isTexture) out.push(v);
    }
    return out;
}

/** GPU estimate: w·h·4 (RGBA8), +~33% for a mip chain (three defaults on). */
function textureBytes(tex: object): number {
    const t = tex as { image?: { width?: number; height?: number }; generateMipmaps?: boolean };
    const w = t.image?.width ?? 0;
    const h = t.image?.height ?? 0;
    if (!w || !h) return 0;
    const base = w * h * 4;
    return Math.round(t.generateMipmaps === false ? base : (base * 4) / 3);
}

/**
 * Copy the json with embedded `data:` image/buffer URIs removed. Strips BEFORE
 * cloning so megabytes of base64 are never duplicated; falls back to the shallow
 * copy if structuredClone is unavailable. Never returns the live host json.
 */
function stripDataUris(json: Record<string, unknown>): unknown {
    const stripArray = (arr: unknown): unknown =>
        Array.isArray(arr)
            ? arr.map((e) => {
                  const item = e as { uri?: unknown };
                  return typeof item?.uri === "string" && item.uri.startsWith("data:")
                      ? { ...item, uri: undefined }
                      : item;
              })
            : arr;

    const shallow: Record<string, unknown> = { ...json };
    if ("images" in shallow) shallow.images = stripArray(shallow.images);
    if ("buffers" in shallow) shallow.buffers = stripArray(shallow.buffers);

    try {
        return structuredClone(shallow);
    } catch {
        return shallow; // best-effort: still stripped, just not fully decoupled
    }
}
